<template>
    <div>
        <Loader :loader="isPageLoading" />
        <div v-show="!isPageLoading">
            <b-form @submit.prevent="onSubmit" @reset.prevent="onReset" novalidate>
                <b-row>
                    <b-col>
                        <Title
                            :label="`${$t('form.label.name')}*`"
                            :state="chkState('title')"
                            :value.sync="$v.form.title.$model"
                            :disabled="!isAllowedToCreateOrEdit"
                            :placeholder="$t('form.placeholder.whatDoYouWantToAchieve')"
                        >
                            <span v-if="!$v.form.title.required">
                                {{ $t('form.validations.required') }}
                            </span>
                            <span v-else-if="!$v.form.title.maxLength">
                                {{
                                    $t('form.validations.maxLength', {
                                        value: '255',
                                    })
                                }}
                            </span>
                        </Title>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <Tags
                            :state="chkState('tags')"
                            :value.sync="$v.form.tags.$model"
                            :disabled="!isAllowedToCreateOrEdit"
                            @ready="tagsReady = $event"
                        />
                    </b-col>
                </b-row>

                <b-row>
                    <b-col md="6" xl="6" sm="6">
                        <b-form-group :label="`${$t('pdis.form.dueDate')}*`" label-for="input-due-date">
                            <v-date-picker
                                :min-date="new Date()"
                                v-model="form.dueDate"
                                nav-visibility="click"
                                :popover="{ visibility: 'click' }"
                            >
                                <template v-slot="{ inputValue, inputEvents }">
                                    <b-form-input
                                        type="text"
                                        :value="inputValue"
                                        v-on="inputEvents"
                                        id="input-due-date"
                                        @change.native="updateValue($event.target.value)"
                                        autocomplete="off"
                                        :state="chkState('dueDate')"
                                        readonly
                                    ></b-form-input>
                                    <b-form-invalid-feedback id="due-date-feedback">
                                        <span v-if="!$v.form.dueDate.required">
                                            {{ $t('form.validations.required') }}
                                        </span>
                                    </b-form-invalid-feedback>
                                </template>
                            </v-date-picker>
                        </b-form-group>
                    </b-col>

                    <b-col md="6" xl="6" sm="6">
                        <b-form-group :label="`${$t('pdis.form.budget')}*`" label-for="input-budget">
                            <b-form-input
                                type="text"
                                id="input-budget"
                                aria-describedby="input-budget"
                                v-model.trim="$v.form.budget.$model"
                                class="mr-2"
                                :state="chkState('budget')"
                            ></b-form-input>
                            <b-form-invalid-feedback id="input-budget">
                                <span v-if="!$v.form.budget.required">{{ $t('form.validations.required') }}</span>
                                <span v-if="!$v.form.budget.decimal">{{ $t('form.validations.decimal') }}</span>
                                <span v-if="!$v.form.budget.maxLength">{{
                                    $t('form.validations.maxLength', {
                                        value: '255',
                                    })
                                }}</span>
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </b-col>

                    <b-col>
                        <c-description
                            :state="chkState('description')"
                            :value.sync="$v.form.description.$model"
                            :label="$t('form.label.description')"
                            :placeholder="$t('form.placeholder.description')"
                        />
                    </b-col>
                </b-row>

                <ButtonLoader
                    class="float-right ml-1"
                    type="submit"
                    variant="primary"
                    :disabled="btnLoader || !isAllowedToCreateOrEdit"
                    :loader="btnLoader"
                >
                    {{ $t('form.button.save') }}
                </ButtonLoader>
                <b-button class="float-right" variant="secondary" :disabled="btnLoader" @click="cancel">
                    {{ $t('form.button.cancel') }}
                </b-button>
            </b-form>
        </div>
    </div>
</template>

<script>
import { ActivePeriod, Description, Tags, Owner, Title } from '@/views/components/fields'
import { mapperToOptions } from '@/views/components/selects'
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import Loader from '@/views/components/loader'
import ButtonLoader from '@/views/components/button-loader'
import { mapperActionPlanToForm } from '../mappers/mapperActionPlanToForm'
import { DatePicker as VDatePicker } from 'v-calendar'
import { getParam as getUrlParam } from '@/lib/url'
import { Description as CDescription } from '@/views/components/fields'

const initialState = {
    id: null,
    title: '',
    tags: [],
    dueDate: null,
    budget: null,
    pdi_id: null,
    description: null,
}

export default {
    name: 'Form',
    components: {
        ButtonLoader,
        Loader,
        ActivePeriod,
        Description,
        Tags,
        Owner,
        VDatePicker,
        Title,
        CDescription,
    },
    props: ['actionPlan', 'actionPlanPromise', 'isEditing', 'canEdit', 'canCreate', 'moduleForm'],
    mixins: [validationMixin],
    data() {
        return {
            form: Object.assign({}, initialState),
            btnLoader: false,
            tagsReady: false,
            activePeriodReady: false,
            defaultOwnerId: null,
        }
    },
    mounted() {
        const {
            query: { ownerId },
        } = this.$route
        this.defaultOwnerId = ownerId
        this.pdi_id = getUrlParam('id', this)
    },
    validations: {
        form: {
            pdi_id: {},
            title: {
                required,
                maxLength: maxLength(255),
            },
            tags: {
                $each: {
                    value: {
                        required,
                    },
                    label: {
                        required,
                    },
                },
            },
            dueDate: {
                required,
            },
            budget: {
                required,
                maxLength: maxLength(255),
            },
            description: {},
        },
    },
    computed: {
        isValid() {
            return !this.$v.form.$anyError
        },
        isDirty() {
            return this.$v.form.$anyDirty
        },
        isPageLoading() {
            return !this.tagsReady
        },
        isAllowedToCreateOrEdit() {
            if (this.isEditing) {
                return this.canEdit
            }
            return this.canCreate
        },
    },
    methods: {
        onSubmit() {
            if (this.validate()) {
                this.$nextTick(() => {
                    this.$emit('onSubmit', this.form, this.onReset, this.isLoading)
                })
            }
        },
        isLoading(state) {
            this.btnLoader = state
        },
        onReset() {
            this.form = Object.assign({}, initialState)
            this.$nextTick(() => {
                this.$v.$reset()
            })
        },
        chkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        validate() {
            this.$v.$touch()
            return this.isValid
        },
        cancel() {
            this.$emit('cancel')
        },
    },
    watch: {
        actionPlan(actionPlan) {
            const actionPlanData = mapperActionPlanToForm(actionPlan, mapperToOptions)
            this.form = Object.assign({}, actionPlanData)
        },
        isPageLoading() {
            this.actionPlanPromise &&
                this.actionPlanPromise.then(() => {
                    const newFormInitialState = mapperActionPlanToForm(this.actionPlan, mapperToOptions)
                    this.form = Object.assign({}, newFormInitialState)
                })
        },
    },
}
</script>

<style scoped>
.btn.disabled {
    cursor: auto;
}

.form-control[readonly] {
    background-color: initial;
}
</style>
